import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

const OutstandingStatement = () => {
  const [reportList, setReportList] = useState([]);
  const [totals, setTotals] = useState({ total_debit: 0, total_credit: 0, total_balance: 0 });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [consigneeId, setConsigneeId] = useState("");

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (consigneeId) payload.consignee_id = consigneeId;

      const response = await post("/reports/party_wise_outstanding_statement/list", payload);
      if (response.status === 200) {
        setReportList(response.data || []);
        setTotals(response.totals || { total_debit: 0, total_credit: 0, total_balance: 0 });
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [showAlert, consigneeId]);

  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
        setConsigneeOptions([
          { value: "", label: "Select Consignee" },
          ...response.data.map(item => ({
            value: item.consignees_id,
            label: item.consignees_name,
          }))
        ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchConsigneeData();
  }, [fetchData, fetchConsigneeData]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", accessorKey: "si_no", enableColumnFilter: false, enableSorting: false },
      { header: "Job No", accessorKey: "job_no" },
      { header: "Consignee", accessorKey: "consignee" },
      { header: "BL No", accessorKey: "bl_number" },
      { header: "TC", accessorKey: "no_of_containers", isTotal: true },
      { header: "20'", accessorKey: "count_20ft" },
      { header: "40'", accessorKey: "count_40ft" },
      { header: "LCL", accessorKey: "count_lcl" },
      {
        header: "Bill No / Date", accessorKey: "bill_number",
        cell: ({ row }) => `${row.bl_number || "—"} / ${row.bill_date || "—"}`
      },
      { header: "Ageing (Days)", accessorKey: "ageing" },
      {
        header: "Debit",
        accessorKey: "debit",
        label_condition: "currency",
        cell: ({ row }) => (
          <span className="text-danger fw-semibold">
            ₹ {Number(row.debit || 0).toLocaleString("en-IN")}
          </span>
        ),
      },
      {
        header: "Credit",
        accessorKey: "credit",
        label_condition: "currency",
        cell: ({ row }) => (
          <span className="text-success fw-semibold">
            ₹ {Number(row.credit || 0).toLocaleString("en-IN")}
          </span>
        ),
      },
      {
        header: "Balance",
        accessorKey: "balance",
        label_condition: "currency",
        cell: ({ row }) => (
          <span className="text-primary fw-bold">
            ₹ {Number(row.balance || 0).toLocaleString("en-IN")}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Party Wise - Outstanding Statement</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Consignee</label>
              <select
                className="form-select"
                value={consigneeId}
                onChange={(e) => setConsigneeId(e.target.value)}
              >
                {consigneeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={fetchData} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mt-3">
        <div className="col-md-4">
          <div className="card text-center border-danger">
            <div className="card-body">
              <h6 className="text-muted mb-1">Total Debit</h6>
              <h4 className="text-danger mb-0">₹ {Number(totals.total_debit).toLocaleString("en-IN")}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card text-center border-success">
            <div className="card-body">
              <h6 className="text-muted mb-1">Total Credit</h6>
              <h4 className="text-success mb-0">₹ {Number(totals.total_credit).toLocaleString("en-IN")}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card text-center border-primary">
            <div className="card-body">
              <h6 className="text-muted mb-1">Total Balance</h6>
              <h4 className="text-primary mb-0">₹ {Number(totals.total_balance).toLocaleString("en-IN")}</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Party Wise - Outstanding Statement</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Party Wise Outstanding Statement"
            columns={columns}
            data={reportList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>
    </>
  );
};

export default OutstandingStatement;
